import { useEffect, useState } from "react";
import {
  View,
  Menu,
  Edit,
  Trash,
  Plus,
  Printer,
  FileDown,
  Upload,
  MoreVertical,
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import toast, { Toaster } from "react-hot-toast";

import Header from "../components/Header";
import LeftSidebar from "../components/LeftSidebar";
import ITEquipmentModal from "../components/ITEquipementModal";
import ITBulkUploadModal from "../components/ITBulkUploadModal";
import ITViewModal from "../components/ITViewModal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";


import api from "../../connector/api";

export default function Projectors() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const [formData, setFormData] = useState({
    id: null,
    description: "",
    category: "",
    serial_number: "",
    cost: "",
    date_of_purchase: "",
    location: "",
    responsible_person: "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch equipment
  const fetchEquipment = async () => {
    try {
      const res = await api.get("it-equipment/");
      // Only keep category "Projector"
      const networkEquipment = res.data.filter(
        (eq) => eq.category === "Projector"
      );
      setEquipment(networkEquipment);
    } catch (err) {
      toast.error("Failed to fetch equipment");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchEquipment();
  }, []);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.id) {
        await api.put(`it-equipment/${formData.id}/`, formData);
        toast.success("Equipment updated!");
      } else {
        await api.post("it-equipment/", formData);
        toast.success("Equipment added!");
      }
      fetchEquipment();
      setIsModalOpen(false);
      resetForm();
    } catch (err) {
      toast.error("Error saving equipment");
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    try {
      await api.delete(`it-equipment/${id}/`);
      toast.success("Equipment deleted");
      fetchEquipment();
    } catch (err) {
      toast.error("Failed to delete equipment");
    }
  };

  const resetForm = () => {
    setFormData({
      id: null,
      description: "",
      category: "",
      serial_number: "",
      cost: "",
      date_of_purchase: "",
      location: "",
      responsible_person: "",
    });
  };

  // Filtered equipment by search
  const filteredEquipment = equipment.filter(
    (eq) =>
      eq.description.toLowerCase().includes(search.toLowerCase()) ||
      eq.category.toLowerCase().includes(search.toLowerCase()) ||
      eq.serial_number.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredEquipment.length / itemsPerPage);
  const currentItems = filteredEquipment.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  // Handle Export
  const handleExport = () => {
    if (!equipment.length) {
      toast.error("No data to export!");
      return;
    }

    // Map your equipment data
    const exportData = equipment.map((eq) => ({
      Description: eq.description,
      Category: eq.category,
      Model: eq.model,
      Serial: eq.serial_number,
      Cost: eq.cost,
      "Purchase Date": eq.date_of_purchase,
      Location: eq.location,
      Responsible: eq.responsible_person_name || "N/A",
    }));

    // Create a worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Create a workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Network Equipment");

    // Generate buffer
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

    // Save file
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `Network_Equipment_${new Date().toISOString().split("T")[0]}.xlsx`);

    toast.success("Excel exported successfully!");
  };

  // Handle Print
  const handlePrint = () => {
    const printWindow = window.open("", "_blank");

    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleString(); // e.g., 9/14/2025, 9:30:15 AM

    printWindow.document.write(`
      <html>
        <head>
          <title>Network Equipment Report</title>
          <link href="https://cdn.jsdelivr.net/npm/tailwindcss@3.3.2/dist/tailwind.min.css" rel="stylesheet">
          <style>
            @media print {
              body { -webkit-print-color-adjust: exact; font-family: sans-serif; }
              table { page-break-inside: auto; border-collapse: collapse; }
              tr { page-break-inside: avoid; page-break-after: auto; }
              th, td { border: 1px solid #d1d5db; padding: 0.5rem; }
            }
            th { background-color: #f3f4f6; font-weight: bold; }
          </style>
        </head>
        <body class="p-6">
          <div class="flex justify-between items-center mb-6 text-xs">
            <div class="flex items-center gap-4">
              <img src="/logo.png" alt="Logo" class="h-12 w-12 object-contain"/>
              <div>
                <p class="text-sm">Network Equipment Inventory Report</p>
              </div>
            </div>
            <div class="text-sm text-gray-700">
              Printed: ${formattedDate}
            </div>
          </div>

          <table class="min-w-full border border-gray-300 text-sm">
            <thead>
              <tr>
                <th>Description</th>
                <th>Serial</th>
                <th>Cost</th>
                <th>Purchase Date</th>
                <th>Location</th>
                <th>Responsible</th>
              </tr>
            </thead>
            <tbody>
              ${equipment
                .map(
                  (eq) => `
                <tr>
                  <td>${eq.description}</td>
                  <td>${eq.serial_number}</td>
                  <td>$${eq.cost}</td>
                  <td>${eq.date_of_purchase}</td>
                  <td>${eq.location}</td>
                  <td>${eq.responsible_person_name || "N/A"}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
          <footer>
            &copy; ${currentDate.getFullYear()} Cambodia Adventist International School. All rights reserved.
          </footer>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Full width header */}
      <header className="w-full border-b bg-white shadow">
        <Header />
      </header>

      <div className="flex flex-1">
        {sidebarOpen && (
          <aside className="w-64 border-r bg-gray-50">
            <LeftSidebar />
          </aside>
        )}

        <main className="flex-1 p-4">
          <div className="max-w-8xl mx-auto p-4">
            {/* Header + Action dropdown */}
            <div className="bg-white p-2 rounded shadow flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <button
                  className="p-2 rounded hover:bg-gray-100"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                >
                  <Menu className="h-5 w-5 text-gray-600" />
                </button>
                <h1 className="text-xl font-bold">Projectors</h1>
              </div>

              {/* Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setActionDropdownOpen(!actionDropdownOpen)}
                  className="p-2 rounded hover:bg-gray-100"
                >
                  <MoreVertical className="h-5 w-5 text-gray-700" />
                </button>

                {actionDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg z-50">
                    <ul className="py-1">
                      <li>
                        <button
                          onClick={() => {
                            resetForm();
                            setIsModalOpen(true);
                            setActionDropdownOpen(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-[#002855] hover:bg-gray-100"
                        >
                          <Plus className="h-4 w-4 mr-2" /> Add Equipment
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => {
                            handleExport();
                            setActionDropdownOpen(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-green-600 hover:bg-gray-100"
                        >
                          <FileDown className="h-4 w-4 mr-2" /> Export
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => {
                            handlePrint();
                            setActionDropdownOpen(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          <Printer className="h-4 w-4 mr-2" /> Print
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => {
                            setBulkModalOpen(true);
                            setActionDropdownOpen(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-[#002855] hover:bg-gray-100"
                        >
                          <Upload className="h-4 w-4 mr-2" /> Bulk Upload
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Search */}
            <input
              type="text"
              placeholder="Search..."
              className="border p-2 rounded w-full mb-4"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />

            {/* Table */}
            {loading ? (
              <p>Loading...</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-300 text-sm" id="equipment-table">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border px-4 py-2 text-left">Description</th>
                      <th className="border px-4 py-2 text-left hidden md:table-cell">Serial</th>
                      <th className="border px-4 py-2 text-left">Cost</th>
                      <th className="border px-4 py-2 text-left hidden lg:table-cell">Purchase Date</th>
                      <th className="border px-4 py-2 text-left hidden lg:table-cell">Location</th>
                      <th className="border px-4 py-2 text-left hidden lg:table-cell">Responsible</th>
                      <th className="border px-4 py-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.length > 0 ? (
                      currentItems.map((eq) => (
                        <tr key={eq.id} className="hover:bg-gray-50">
                          <td className="border px-4 py-2 font-medium">{eq.description}</td>
                          <td className="border px-4 py-2 hidden md:table-cell">{eq.serial_number}</td>
                          <td className="border px-4 py-2">${eq.cost}</td>
                          <td className="border px-4 py-2 hidden lg:table-cell">{eq.date_of_purchase}</td>
                          <td className="border px-4 py-2 hidden lg:table-cell">{eq.location}</td>
                          <td className="border px-4 py-2 hidden lg:table-cell">
                            {eq.responsible_person_name || "N/A"}
                          </td>
                          <td className="border-t px-4 py-2 flex gap-2">
                            <button
                              onClick={() => {
                                setSelectedEquipment(eq);
                                setViewModalOpen(true);
                              }}
                              className="bg-blue-600 text-white px-3 py-1 rounded flex gap-1"
                            >
                              <View className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setFormData(eq);
                                setIsModalOpen(true);
                              }}
                              className="bg-yellow-500 text-white px-3 py-1 rounded flex gap-1"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setItemToDelete(eq);
                                setDeleteModalOpen(true);
                              }}
                              className="bg-red-600 text-white px-3 py-1 rounded flex gap-1"
                            >
                              <Trash className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-4 text-gray-500">
                          No equipment found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-between items-center mt-2 text-sm">
                    <button
                      onClick={handlePrev}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 rounded ${
                        currentPage === 1 ? "bg-gray-200" : "bg-gray-300 hover:bg-gray-400"
                      }`}
                    >
                      Prev
                    </button>
                    <span>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={handleNext}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 rounded ${
                        currentPage === totalPages ? "bg-gray-200" : "bg-gray-300 hover:bg-gray-400"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>

        {/* Modals */}
        <ITEquipmentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleSubmit}
          formData={formData}
          setFormData={setFormData}
        />
        <ITBulkUploadModal isOpen={bulkModalOpen} onClose={() => setBulkModalOpen(false)} />
        <ITViewModal
          isOpen={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          equipment={selectedEquipment}
        />
        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          itemName={itemToDelete?.description || "this item"}
          onConfirm={() => {
            handleDelete(itemToDelete.id);
            setDeleteModalOpen(false);
          }}
        />
      </div>

      <Toaster position="top-right" />
    </div>
  );
}
